/**
 * <TutorSessionEntry> — une ligne de la session d'un élève avec le champion, vue du masteur (organism).
 *
 * **Compacte, chaque sorte reconnaissable à sa forme sans lire** (Wajih, 04/11 ; la note de recherche :
 * `DECISIONS.md`, « La colonne du champion se lit comme un chat de livestream ») :
 *  - une **trace** : une ligne grise, un point, l'heure au bout — ce qui s'est passé ; un appui la déplie ;
 *  - le **quiz** de l'élève : la même ligne, avec son glyphe et son score en chip — jamais une ligne du champion ;
 *  - le texte du **champion** : un liseré à sa couleur, une ligne (son titre, sinon le début), le reste à la demande ;
 *  - la **bulle** du masteur, à droite, à l'aplat de son univers, l'heure à sa gauche ;
 *  - ce qui attend une décision est une **carte** (la génération : `TutorSessionGeneration.tsx`) ;
 *  - le champion qui travaille (`<WaitingText>`), et le repère « Depuis ton dernier passage ».
 *
 * Composes `<MessageBubble>`, `<GeneratedActivityResults>`, `<Divider>`, `<WaitingText>`, `<Badge>`,
 * `<Text>`. Le champion arrive en prop (`helper`), jamais écrit ici : il signe « <champion> propose ».
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { tutorTraceText } from "../../../ports/primary/ui/formatters/tutorSessionFormatter"
import { type UITutorQuizOutcome, type UITutorSessionEntry } from "../../../ports/primary/ui/types/tutorLive"
import { type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Divider } from "../../atoms/Divider"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { Badge } from "../../atoms/Badge"
import { Banner } from "../../atoms/Banner"
import { Icon } from "../../atoms/icons/Icon"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { GeneratedActivityResults } from "../GeneratedActivityResults"
import { MessageBubble } from "../../molecules/MessageBubble"
import { Modal } from "../../molecules/Modal"
import { ActivityGroup, GenerationProgress, GenerationReady, HelperProposal } from "./TutorSessionGeneration"

/** Ce que le masteur fait d'une activité générée. */
export type TutorSessionResultAction = "create" | "show" | "quiz" | "regenerate" | "retry"

/**
 * Les gestes des lignes de la génération : « Revoir » et l'envoi de la première proposition (la carte « prêtes »),
 * « Voir », « Envoyer » et « Écarter » (« <champion> propose »).
 */
export type TutorSessionGenerationAction = "review" | "sendFirst" | "see" | "send" | "dismiss"

export type TutorSessionEntryProps = {
    entry: UITutorSessionEntry
    /** Le champion qui aide en séance : sa tête et son prénom signent ses lignes. */
    helper: ChampionName
    /**
     * Une activité générée, ajoutée, montrée, son quiz de prérequis basculé, ou régénérée (`activityId` :
     * laquelle des trois).
     */
    onResult?: (entryId: string, action: TutorSessionResultAction, activityId?: string | number) => void
    /** Un geste d'une ligne de la génération (la carte « prêtes », « <champion> propose »). */
    onGeneration?: (entryId: string, action: TutorSessionGenerationAction) => void
    /** Une proposition survolée dans la carte « prêtes » (web) : son aperçu au centre ; `null` quand le survol sort. */
    onPeek?: (proposalId: string | null) => void
    /** La proposition dont l'aperçu est au centre. */
    peekId?: string | null
    /**
     * La carte « prêtes » porte le cadre en dégradé (la prochaine action). Défaut `true` ; `false` là où plusieurs
     * cartes se suivent sur le même squelette (« À traiter », la relecture) : le cadre n'en désigne qu'une par écran.
     */
    framed?: boolean
    testID?: string
}

const TEXTS = {
    since: "Depuis ton dernier passage",
    more: (text: string) => `${text}, lire la suite`,
    show: "Montrer à l'élève",
    regenerate: "Autres énoncés",
    retry: "Réessayer",
    seeLarge: "Voir en grand",
    reading: "Les propositions",
}

/** L'icône d'un réglage dans la bulle : la matière, la difficulté. */
const SETTING_ICON = 14
/** Le ton du glyphe d'un quiz fini, et de sa chip de score. */
const QUIZ_TONE: Record<UITutorQuizOutcome, "success" | "warning" | "neutral"> = {
    passed: "success",
    failed: "warning",
    interrupted: "neutral",
}

/** Mémoïsée : la session se redessine à chaque frappe du champ et à chaque signal du live. */
export const TutorSessionEntry = React.memo(function TutorSessionEntry(props: TutorSessionEntryProps) {
    const { entry, helper, onResult, onGeneration, onPeek, peekId, framed, testID } = props
    const [open, setOpen] = useState(false)

    switch (entry.kind) {
        case "since":
            return <Divider label={TEXTS.since} color="role" spacing="1" testID={testID} />
        case "trace":
            return (
                <Line
                    at={entry.at}
                    open={open}
                    onToggle={() => setOpen(!open)}
                    mark={<View style={styles.traceDot} />}
                    testID={testID}
                >
                    <Text
                        size="text-xs"
                        color="text-muted"
                        numberOfLines={open ? 0 : 1}
                        style={entry.quiet === true ? styles.quiet : undefined}
                    >
                        {tutorTraceText(entry)}
                    </Text>
                </Line>
            )
        case "quiz": {
            const tone = QUIZ_TONE[entry.outcome]
            return (
                <Line
                    at={entry.at}
                    open={open}
                    onToggle={() => setOpen(!open)}
                    mark={<Icon name="clipboard-check" size={12} color={tone === "neutral" ? "text-muted" : tone} />}
                    trailing={entry.score != null ? <Badge text={entry.score} variant={tone} tinted /> : undefined}
                    testID={testID}
                >
                    <Text size="text-xs" color="text-primary" numberOfLines={open ? 0 : 1}>
                        {entry.text}
                    </Text>
                </Line>
            )
        }
        case "working":
            return (
                <View testID={testID}>
                    <WaitingText size="text-xs" align="start">
                        {entry.text}
                    </WaitingText>
                </View>
            )
        case "helper": {
            // Le texte du champion, sans sa tête ni son nom (ils sont une fois en haut de la colonne) : son liseré le
            // signe. Une ligne — son titre, sinon le début du texte ; un appui déplie le reste.
            const headline = entry.title ?? entry.text
            return (
                <Pressable
                    onPress={() => setOpen(!open)}
                    accessibilityRole="button"
                    accessibilityLabel={TEXTS.more(headline)}
                    aria-expanded={open}
                    style={styles.helper}
                    testID={testID}
                >
                    <Stack gap="1">
                        <Row gap="2" align="center">
                            <Text
                                size="text-sm"
                                weight={entry.title != null ? "semibold" : "regular"}
                                color="text-strong"
                                numberOfLines={open ? 0 : 1}
                                style={styles.flex}
                            >
                                {headline}
                            </Text>
                            <Text size="text-xxs" color="text-muted">
                                {formatTime(entry.at)}
                            </Text>
                        </Row>
                        {open && entry.title != null && (
                            <Text size="text-sm" color="text-primary">
                                {entry.text}
                            </Text>
                        )}
                    </Stack>
                </Pressable>
            )
        }
        case "progress":
            return <GenerationProgress entry={entry} testID={testID} />
        case "ready":
            return (
                <GenerationReady
                    entry={entry}
                    onReview={onGeneration != null ? () => onGeneration(entry.id, "review") : undefined}
                    onSendFirst={onGeneration != null ? () => onGeneration(entry.id, "sendFirst") : undefined}
                    onPeek={onPeek}
                    peekId={peekId}
                    framed={framed}
                    testID={testID}
                />
            )
        case "proposal":
            return (
                <HelperProposal
                    entry={entry}
                    helper={helper}
                    onSee={onGeneration != null ? () => onGeneration(entry.id, "see") : undefined}
                    onSend={onGeneration != null ? () => onGeneration(entry.id, "send") : undefined}
                    onDismiss={onGeneration != null ? () => onGeneration(entry.id, "dismiss") : undefined}
                    testID={testID}
                />
            )
        case "activity":
            return <ActivityGroup entry={entry} testID={testID} />
        case "tutor": {
            const settings = entry.settings ?? []
            // La bulle du masteur, à droite : sa forme le dit, l'heure à sa gauche, en petit.
            return (
                <Row gap="2" align="end" justify="end" testID={testID}>
                    <Text size="text-xxs" color="text-muted">
                        {formatTime(entry.at)}
                    </Text>
                    <View style={styles.bubble}>
                        {settings.length > 0 ? (
                            // Round 4 : « l'information principale, c'est presque la configuration » — elle
                            // d'abord, lisible ; le texte éventuel dessous.
                            <MessageBubble face="own" tail>
                                <Stack gap="1">
                                    <Row gap="2" style={styles.wrap}>
                                        {settings.map((setting) => (
                                            <Row key={setting.field} gap="1" align="center">
                                                {setting.iconUri != null && (
                                                    <ImageIcon uri={setting.iconUri} size={SETTING_ICON} plate />
                                                )}
                                                <Text size="text-xs" weight="bold" color="text-on-role">
                                                    {setting.label}
                                                </Text>
                                            </Row>
                                        ))}
                                    </Row>
                                    {entry.text !== "" && (
                                        // Deux lignes, un appui déplie le reste, comme une trace.
                                        <Pressable onPress={() => setOpen(!open)} aria-expanded={open}>
                                            <Text size="text-xs" color="text-on-role" numberOfLines={open ? 0 : 2}>
                                                {entry.text}
                                            </Text>
                                        </Pressable>
                                    )}
                                </Stack>
                            </MessageBubble>
                        ) : (
                            <MessageBubble face="own" text={entry.text} tail />
                        )}
                    </View>
                </Row>
            )
        }
        case "result":
            return <TutorSessionResult entry={entry} onResult={onResult} testID={testID} />
    }
})

/**
 * Une ligne de fait (une trace, un quiz) : sa marque, son texte sur une ligne, sa chip et son heure au bout ; un
 * appui la déplie. La forme commune de ce qui s'est passé.
 */
function Line(props: {
    at: string
    open: boolean
    onToggle: () => void
    mark: React.ReactNode
    trailing?: React.ReactNode
    children: React.ReactNode
    testID?: string
}) {
    return (
        <Pressable onPress={props.onToggle} aria-expanded={props.open} testID={props.testID}>
            <Row gap="2" align={props.open ? "start" : "center"}>
                <View style={styles.mark}>{props.mark}</View>
                <View style={styles.flex}>{props.children}</View>
                {props.trailing}
                <Text size="text-xxs" color="text-muted">
                    {formatTime(props.at)}
                </Text>
            </Row>
        </Pressable>
    )
}

/**
 * Une génération sans proposition : la phrase, et « Réessayer ». Des propositions sans relecture (une ligne ancienne) :
 * en version courte, « Voir en grand » les relit côte à côte.
 */
function TutorSessionResult(props: {
    entry: Extract<UITutorSessionEntry, { kind: "result" }>
    onResult?: TutorSessionEntryProps["onResult"]
    testID?: string
}) {
    const { entry, onResult, testID } = props
    // État d'interface : la boîte de lecture « Voir en grand » est ouverte.
    const [reading, setReading] = useState(false)
    const activities = entry.activities
    const regenerate = onResult != null ? () => onResult(entry.id, "regenerate") : undefined

    if (entry.error != null) {
        return (
            <Stack gap="2" testID={testID}>
                <Banner color="danger" tinted icon="exclamation-circle" message={entry.error} />
                {onResult != null && (
                    <View style={styles.start}>
                        <Button
                            label={TEXTS.retry}
                            iconName="redo"
                            variant="outline"
                            size="sm"
                            onPress={() => onResult(entry.id, "retry")}
                        />
                    </View>
                )}
            </Stack>
        )
    }
    const create = (id: string | number) => onResult?.(entry.id, "create", id)
    // « Montrer à l'élève » depuis la boîte de lecture la referme : le masteur voit au centre ce qu'il vient de
    // montrer. Hors de la boîte, elle est déjà fermée.
    const show = (id: string | number) => {
        onResult?.(entry.id, "show", id)
        setReading(false)
    }
    const quiz = (id: string | number) => onResult?.(entry.id, "quiz", id)
    const quizAvailable = entry.quizAvailable === true && onResult != null
    const showAction = onResult != null ? { label: TEXTS.show, iconName: "eye" as const } : undefined
    const anyReady = activities.some((activity) => activity.status === "ready")
    // Les mêmes propositions, entières (la colonne agrandie, la boîte « Voir en grand ») ou courtes (la colonne).
    const results = (compact: boolean) => (
        <GeneratedActivityResults
            activities={activities}
            voice="student"
            compact={compact}
            onCreate={create}
            quizAvailable={quizAvailable}
            onToggleQuiz={quiz}
            extraAction={showAction != null ? { ...showAction, onPress: show } : undefined}
        />
    )
    return (
        <Stack gap="2" testID={testID}>
            {
                // Weekly du 03/11 (W10) : chaque proposition en version courte, qu'on donne à l'élève sans tout
                // lire ; « Voir en grand » les relit côte à côte, dans une boîte de lecture.
                <>
                    {results(true)}
                    {anyReady && (
                        <View style={styles.start}>
                            <Button
                                label={TEXTS.seeLarge}
                                iconName="expand-alt"
                                variant="outline"
                                size="sm"
                                onPress={() => setReading(true)}
                            />
                        </View>
                    )}
                    <Modal visible={reading} onClose={() => setReading(false)} title={TEXTS.reading} wide>
                        {results(false)}
                    </Modal>
                </>
            }
            {onResult != null && anyReady && (
                <Row gap="2" align="center" justify="center" style={styles.wrap}>
                    <Button label={TEXTS.regenerate} iconName="redo" variant="ghost" size="sm" onPress={regenerate} />
                </Row>
            )}
        </Stack>
    )
}

const MARK = 14

const styles = StyleSheet.create((theme) => ({
    flex: { flex: 1, minWidth: 0 },
    /** Une suggestion rangée sans décision : plus discrète qu'une trace. */
    quiet: { fontStyle: "italic" as const },
    start: { alignItems: "flex-start" as const },
    bubble: { maxWidth: "82%" },
    wrap: { flexWrap: "wrap" as const },
    /** La marque d'une ligne de fait : une case fixe, que les traces et les quiz partagent. */
    mark: { width: MARK, height: MARK, alignItems: "center" as const, justifyContent: "center" as const },
    traceDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: theme.colors.border },
    /** Le texte du champion : son liseré, à la couleur de ses points sur la barre de la séance. */
    helper: {
        paddingLeft: theme.spacing["2"],
        paddingVertical: theme.spacing["1"],
        borderLeftWidth: 2,
        borderLeftColor: theme.colors.info,
    },
}))
